/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useLayoutEffect, useMemo, useRef} from 'react';
import {VariableSizeList as List} from 'react-window';

const DEFAULT_ITEM_HEIGHT = 100;
const LISTBOX_MAX_HEIGHT  = 8;
const PREFETCH_ROW_MARGIN = 5;

interface JobTaskLMUConfigListsProps {
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
};

type JobTaskLMUConfigListsResults = {
  JobTaskLMUConfigLists: React.ComponentType
};

interface ListItem {
  index: number;
  style: any;
  data: any;
};

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef(function OuterElementType(props, ref) {
  const outerProps = React.useContext(OuterElementContext);

  return (
    <ul ref={ref} {...props} {...outerProps} />
  );
});

/**
 * Sums the measured (or estimated) height of every row, so the listbox viewport
 * is only ever as tall as its actual content - capped at the usual max height for
 * longer lists, where the viewport should scroll rather than keep growing.
 */
const getMeasuredListHeight = (
  itemCount: number,
  getItemSize: (index: number) => number
): number => {
  let totalHeight = 0;
  for (let index = 0; index < itemCount; index++) {
    totalHeight += getItemSize(index);
  }
  return Math.min(totalHeight, LISTBOX_MAX_HEIGHT * DEFAULT_ITEM_HEIGHT);
};

/**
 * This hook provides a reusable list component that virtualizes content rendering
 * using measured, per-row item heights. It ensures smoother performance for longer
 * lists by only rendering visible rows, while still growing a row's height to fit
 * its full (unclipped, wrapped) label rather than overlapping adjacent rows.
 *
 */
export const useJobTaskLMUConfigLists = ({
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false
}: JobTaskLMUConfigListsProps): JobTaskLMUConfigListsResults => {
  const listRef           = useRef<List>(null);
  const savedScrollOffset = useRef(0);
  const itemHeights       = useRef(new Map<number, number>());
  const pendingResetIndex = useRef<number | null>(null);

  const getItemSize = useCallback((index: number) => (
    itemHeights.current.get(index) ?? DEFAULT_ITEM_HEIGHT
  ), []);

  const setItemSize = useCallback((index: number, size: number) => {
    if (itemHeights.current.get(index) !== size) {
      itemHeights.current.set(index, size);
      const isResetAlreadyQueued = pendingResetIndex.current !== null;
      pendingResetIndex.current = isResetAlreadyQueued
        ? Math.min(pendingResetIndex.current as number, index)
        : index;
      if (!isResetAlreadyQueued) {
        requestAnimationFrame(function() {
          if (pendingResetIndex.current !== null) {
            listRef.current?.resetAfterIndex(pendingResetIndex.current);
            pendingResetIndex.current = null;
          }
        });
      }
    }
  }, []);

  const renderList = useCallback(function Row({
    index,
    style,
    data
  }: ListItem) {
    const contentRef = useRef<HTMLDivElement>(null);
    const option      = data?.[index];

    useLayoutEffect(function() {
      const node = contentRef.current;
      const row  = node?.closest('li');
      if (node && row) {
        const updateSize = function() {
          const {paddingTop, paddingBottom, minHeight} = window.getComputedStyle(row);
          const verticalPadding = parseFloat(paddingTop) + parseFloat(paddingBottom);
          const rowMinHeight    = parseFloat(minHeight) || 0;
          const contentHeight   = node.getBoundingClientRect().height + verticalPadding;
          setItemSize(index, Math.max(contentHeight, rowMinHeight));
        };
        updateSize();
        const observer = new ResizeObserver(updateSize);
        observer.observe(node);
        return function() {
          observer.disconnect();
        };
      }
      return undefined;
    }, [index, setItemSize]);

    return React.cloneElement(option, {
      style : {
        ...option?.props?.style,
        ...style
      }
    }, (
      <div ref={contentRef}>
        {option?.props?.children}
      </div>
    ));
  }, [setItemSize]);

  const JobTaskLMUConfigLists = useMemo(() => {
    return React.forwardRef(function Listbox(props, ref) {
      const {
        children,
        ...other
      } = props || {};

      const itemCount  = children?.length;
      const listHeight = getMeasuredListHeight(itemCount, getItemSize);

      const handleScroll = function({scrollOffset}) {
        savedScrollOffset.current = scrollOffset;
      };

      // triggers prefetch once the visible window nears the end of the currently
      // loaded rows - based on rendered row indices rather than pixel offsets, so
      // it isn't affected by (measured or estimated) row height ...
      const handleItemsRendered = function({visibleStopIndex}) {
        if (
          hasNextPage &&
          !isFetchingNextPage &&
          visibleStopIndex >= itemCount - PREFETCH_ROW_MARGIN
        ) {
          fetchNextPage?.();
        }
      };

      return (
        <OuterElementContext.Provider value={other}>
          <div ref={ref} role={'listbox'}>
            <List ref={listRef}
              height={listHeight}
              width={'auto'}
              itemCount={itemCount}
              itemSize={getItemSize}
              itemData={children}
              onScroll={handleScroll}
              onItemsRendered={handleItemsRendered}
              outerElementType={OuterElementType}
              initialScrollOffset={savedScrollOffset.current}
              {...other}>
              {renderList}
            </List>
          </div>
        </OuterElementContext.Provider>
      );
    });
  }, [hasNextPage, fetchNextPage, isFetchingNextPage, renderList, getItemSize]);

  return {
    JobTaskLMUConfigLists
  };
};
